import {
  Box,
  Grid,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import Button from "@mui/material/Button";
import { saveAs } from "file-saver";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import downloadIcon_dark from "../../assets/USA_Images/downloadIcon_dark.svg";
import downloadIcon_light from "../../assets/USA_Images/downloadIcon_light.svg";
import { ReactComponent as EditChecklistIcon } from "../../assets/USA_Images/EditIcon.svg";
import CustomTextfield from "../../components/CustomInputs/CustomTextfield";
import CancelButtons from "../../components/DialogBoxComponent/CancelButtons";
import SubmitButtons from "../../components/DialogBoxComponent/SubmitButtons";
import HeaderToolbar from "../../components/HeaderToolbar";
import Toast from "../../components/ToastContainer/CustomToast";
import {
  generateAccessKey,
  getClientData,
  saveClientData,
} from "../../slices/AtlasIntegration/AtlasIntegrationSlice";
import { ThemeContext } from "../../theme/ThemeContext";
import CommonUtil from "../../Util/CommonUtils";

export default function AtlasIntegration() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const companyId = localStorage.getItem("companyId");
  const [editMode, setEditMode] = useState(false);
  const [isDataEditable, setIsDataEditable] = useState(false);
  const [isDownloadable, setIsDownloadable] = useState(false);
  const [noteable, setNoteable] = useState(false);
  const { darkMode } = useContext(ThemeContext);

  const [payload, setPayload] = useState({
    id: "",
    clientId: "",
    companyId: companyId,
    apiAccessKey: "",
    ipAddressList: "",
    callBackURL: "",
  });

  const [error, setError] = React.useState({
    id: false,
    clientId: false,
    companyId: false,
    apiAccessKey: false,
    ipAddressList: false,
    callBackURL: false,
  });

  useEffect(() => {
    loadClientData(companyId);
  }, []);

  const generateAPIAccessKey = () => {
    dispatch(generateAccessKey())
      .unwrap()
      .then((data) => {
        if (data.code === "MSGS4001" && data.data) {
          Toast(data.message, "success");
          setPayload({
            ...payload,
            clientId: data.data.clientId,
            apiAccessKey: data.data.apiAccessKey,
          });
          setIsDownloadable(true);
          setNoteable(false);
        } else {
          Toast(data.message, "error");
        }
      })
      .catch((er) => { });
  };

  const handleSave = () => {
    if (
      CommonUtil.isEmpty(payload.clientId) ||
      CommonUtil.isEmpty(payload.apiAccessKey)
    ) {
      Toast(t("Please generate client Id and API key."), "error");
      setError({ ...error, code: true });
      return false;
    }

    if (CommonUtil.isEmpty(payload.callBackURL)) {
      Toast(t("Please enter call back URL."), "error");
      setError({ ...error, code: true });
      return false;
    }

    if (CommonUtil.isEmpty(payload.ipAddressList)) {
      Toast(t("Please enter IP Adress."), "error");
      setError({ ...error, code: true });
      return false;
    }

    dispatch(saveClientData(payload))
      .unwrap()
      .then((data) => {
        if (data.code === "MSGS4002") {
          Toast(data.message, "success");
          setPayload({
            ...payload,
            clientId: data.data.clientId,
            apiAccessKey: data.data.apiAccessKey,
            callBackURL: data.data.callBackURL,
            ipAddressList: data.data.ipAddressList.toString(),
          });
          setEditMode(true);
          setIsDataEditable(true);
        } else {
          Toast(data.message, "error");
        }
      });
  };

  const handleCancel = () => {
    loadClientData(companyId);
  };

  const handleDownload = () => {
    const fileData = new Blob(
      [
        "Client Id= " +
        payload.clientId +
        ",\n" +
        "Access Key= " +
        payload.apiAccessKey,
      ],
      { type: "text/plain;charset=utf-8" }
    );
    saveAs(fileData, "client_data.txt");
  };

  const handleEdit = () => {
    setIsDataEditable(false);
  };

  const handleChange = (event) => {
    const name = event.target.name;
    let value = event.target.value;
    setPayload({
      ...payload,
      [name]: value,
    });
    setError({
      [name]: false,
    });
  };

  const loadClientData = (companyId) => {
    dispatch(getClientData(companyId))
      .unwrap()
      .then((data) => {
        if (data.code === "MSGS4003" && data.data) {
          setPayload({
            ...payload,
            clientId: data.data.clientId,
            apiAccessKey: data.data.apiAccessKey,
            callBackURL: data.data.callBackURL,
            ipAddressList: data.data.ipAddressList.toString(),
          });
          setIsDataEditable(true);
          setEditMode(true);
          setIsDownloadable(true);
        } else {
          setPayload({
            ...payload,
          });
          setNoteable(true);
        }
      })
      .catch((er) => { });
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 100px)",
          backgroundColor: darkMode ? "#021C31" : "#fff",
          overflow: "hidden",
        }}
      >
        <Stack
          direction={"row"}
          style={{
            padding: "8px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <HeaderToolbar title={t("ATLAS_INTEGRATION")} />
          <Button
            onClick={handleEdit}
            sx={{
              height: "40px",
              color: "#FFFFFF",
              opacity: 0.8,
              fontFamily: "font_regular",
              borderRadius: "4px",
              backgroundColor: "#474A4E",
            }}
            startIcon={<EditChecklistIcon />}
          >
            Edit
          </Button>
        </Stack>

        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "100%",
          }}
        >
          <Stack direction={"row"} sx={{ height: "85vh", flex: 1 }}>
            <Box
              sx={{
                backgroundColor: darkMode ? "#021C31" : "#fff",
                width: "65%",
                padding: "10px",
                paddingTop: "0px",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                  <CustomTextfield
                    label={t("CLIENT_ID")}
                    name="clientId"
                    disabled={true}
                    value={payload.clientId}
                    key={`clientId-${darkMode}`}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                  <CustomTextfield
                    label={t("API_KEY")}
                    name="apiKey"
                    value={payload.apiAccessKey}
                    disabled={true}
                    key={`clientId-${darkMode}`}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                  <CustomTextfield
                    type={"text"}
                    label={t("CALL_BACK_URL")}
                    name="callBackURL"
                    handleChange={(e) => handleChange(e)}
                    value={payload.callBackURL}
                    disabled={isDataEditable}
                    required={true}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                  <CustomTextfield
                    type={"text"}
                    label={t("IP_ADDRESS_LIST")}
                    name="ipAddressList"
                    handleChange={(e) => handleChange(e)}
                    value={payload.ipAddressList}
                    disabled={isDataEditable}
                    required={true}
                  />
                </Grid>
                {!isDataEditable && (
                  <Grid
                    item
                    sm={12}
                    padding={2}
                    style={{
                      paddingBottom: "8px",
                    }}
                  >
                    <Stack direction="row" spacing={1}>
                      <Typography
                        style={{
                          color: darkMode ? "#D5D5D5" : "",
                          // opacity: 0.8,
                          fontWeight: "bold",
                        }}
                      >
                        Note :
                      </Typography>
                      <Typography
                        style={{
                          color: darkMode ? "#D5D5D5" : "",
                          // opacity: 0.8,
                          // fontFamily: "font_regular",
                        }}
                      >
                        Please click on generate to auto fetch client ID and API
                        key.
                      </Typography>
                    </Stack>
                  </Grid>
                )}

                {!isDataEditable ? (
                  <Grid item xs={12}>
                    <Button
                      onClick={generateAPIAccessKey}
                      size="small"
                      style={{
                        color: "white",
                        borderColor: "#226BAB",
                        borderRadius: "40px !important",
                        width: "150px",
                        height: "42px",
                        background: "#226BAB",
                      }}
                    >
                      {noteable ? (
                        <Typography
                          // style={{
                          //   fontFamily: "font_regular",
                          // }}
                        >
                          {t("GENERATE")}
                        </Typography>
                      ) : (
                        <Typography
                          // style={{
                          //   fontFamily: "font_regular",
                          // }}
                        >
                          {t("RE-GENERATE")}
                        </Typography>
                      )}
                    </Button>
                  </Grid>
                ) : (
                  ""
                )}
              </Grid>
            </Box>

            <Box
              sx={{
                backgroundColor: darkMode ? "" : "#fff",
                width: "40%",
                height: "100%",
              }}
            >
              <Grid
                container
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  height: "100%",
                }}
              >
                <Grid
                  item
                  style={{
                    bottom: "5px"
                  }}
                >
                  {isDownloadable ? (
                    <Box
                      style={{
                        height: "50px",
                        width: "50px",
                        border: darkMode
                          ? "1px solid #535353"
                          : "1px solid #CACACA",
                        backgroundColor: darkMode ? "#313131" : "#FFFFFF",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: "8px",
                        marginTop: "6px",
                      }}
                    >
                      <Tooltip
                        title={
                          <Typography fontSize={12} fontWeight={500}>
                            Download
                          </Typography>
                        }
                      >
                        <img
                          src={
                            darkMode ? downloadIcon_dark : downloadIcon_light
                          }
                          height="22px"
                          width="22px"
                          onClick={handleDownload}
                        ></img>
                      </Tooltip>
                    </Box>
                  ) : (
                    ""
                  )}
                </Grid>
              </Grid>
            </Box>
          </Stack>
          <Grid container>
            <Grid item xs={12}>
              {!isDataEditable ? (
                <Grid
                  container
                  justifyContent="flex-end"
                  mt={2}
                  spacing={1}
                  sx={{ marginBottom: "10px", padding: "8px" }}
                >
                  <Grid item>
                    <CancelButtons
                      onClick={handleCancel}
                      color="secondary"
                      variant="contained"
                    >
                      <Typography>{t("CANCEL")}</Typography>
                    </CancelButtons>
                  </Grid>
                  <Grid item>
                    <SubmitButtons onClick={handleSave} variant="contained">
                      <Typography>{t("COMMON007")}</Typography>
                    </SubmitButtons>
                  </Grid>
                </Grid>
              ) : (
                ""
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
}
